import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { sellerFeventAppliedearchValueAtom } from '@service/seller/containers/SellerFeventSearchTable/state/sellerFeventSearchTable.atom';
import { FeventByShop } from '@shared/api/fevent/fevent.interface';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/icons';
import { DeathCharacter } from '@shared/miscValues';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { getAgencyBrandName } from '@shared/utils/bugoBrandName.util';
import { formatFeventDateString } from '@shared/utils/formatDate';
import { Table, TableColumnsType } from 'antd';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';

import { SellerShopOrderCreateModal } from '../SellerShopOrderCreateModal/SellerShopOrderCreateModal';

interface Props {
  feventList: FeventByShop[];
}

type SellerFeventSerachTableData = {
  id: string;
  agencyName: string;
  funeralHomeInfo: string;
  roomInfo: string;
  deceasedName: string;
  address: string;
  enterance: string;
  coffinOut: string;
  fevent: FeventByShop;
};

export const SellerFeventSearchTable: FC<Props> = function ({ feventList }: Props) {
  //* 검색어 입력은 SellerFeventSearchBar가 맡는다 (배치를 떼어놓을 수 있게 분리)
  const appliedearchValue = useRecoilValue(sellerFeventAppliedearchValueAtom);
  const [selectedFevent, setSelectedFevent] = useState<FeventByShop | null>(null);
  const { CS } = Icon();
  const createModalOpenId = `sellerShopOrderCreate`;
  const setCreateModalOpen = useSetRecoilState(simpleOpenAtomFamily(createModalOpenId));

  const searchedValue = useMemo(() => {
    const searcher = new FuzzySearch(feventList, [
      'funeralHomeInfo.name',
      'funeralHomeInfo.address',
      'funeralHomeInfoEmbed.name',
      'funeralHomeInfoEmbed.address',
      'memberOrderList.fullName',
      'deceasedInfo.name',
      //* 상조명으로도 검색 (멀티 브랜드 bugoAgencyList는 경로 검색이 안 되므로 대표 이름만)
      'bugoBrand.bugoAgency.nickName',
      'bugoBrand.bugoAgency.corpName',
    ]);
    return searcher.search(appliedearchValue);
  }, [feventList, appliedearchValue]);

  const sellerFeventTableData: SellerFeventSerachTableData[] = useMemo(() => {
    return _.map(searchedValue as FeventByShop[] | null, (fevent) => {
      return {
        id: fevent._id,
        agencyName: getAgencyBrandName(fevent.bugoBrand, fevent.bugoBrandAgencyIndex),
        funeralHomeInfo:
          fevent.funeralHomeInfoEmbed?.name ?? fevent.funeralHomeInfo.name ?? '',
        roomInfo: fevent.roomInfoEmbed?.name ?? '',
        address:
          fevent.funeralHomeInfoEmbed?.address ?? fevent.funeralHomeInfo.address ?? '',
        deceasedName: fevent.deceasedInfo.name ?? '',
        enterance: formatFeventDateString(fevent.deceasedInfo.enterance, false),
        coffinOut: formatFeventDateString(fevent.deceasedInfo.coffinOut, false),
        fevent: fevent,
      };
    });
  }, [searchedValue]);

  const onCallOrderClick = (record: SellerFeventSerachTableData) => {
    setSelectedFevent(record.fevent);
    setCreateModalOpen(true);
  };

  const columns: TableColumnsType<SellerFeventSerachTableData> = [
    {
      title: '상조',
      dataIndex: 'agencyName',
      key: 'agencyName',
      align: 'center',
      render: (agencyName: string) => <p>{agencyName === '' ? '-' : agencyName}</p>,
    },
    {
      title: '장례식장',
      dataIndex: 'funeralHomeInfo',
      key: 'funeralHomeInfo',
      align: 'center',
    },
    {
      title: '호실',
      dataIndex: 'roomInfo',
      key: 'roomInfo',
      align: 'center',
    },
    {
      title: '고인성함',
      dataIndex: 'deceasedName',
      key: 'deceasedName',
      align: 'center',
      render: (name: string) => {
        return (
          <p>
            {DeathCharacter} {name}님
          </p>
        );
      },
    },
    {
      title: '입실일',
      dataIndex: 'enterance',
      key: 'enterance',
      align: 'center',
    },
    {
      title: '발인일',
      dataIndex: 'coffinOut',
      key: 'coffinOut',
      align: 'center',
    },
    {
      title: '전화주문',
      align: 'center',
      render: (_, record) => {
        return (
          <div className="center-box">
            <Button
              className="rounded-sm p-0.5 shadow-sm"
              onClick={() => {
                onCallOrderClick(record);
              }}
            >
              <CS className="wh-6" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {appliedearchValue && (
        <Table
          className="w-full"
          columns={columns}
          dataSource={_.flatMapDeep([sellerFeventTableData])}
          pagination={{ position: ['bottomCenter'] }}
          rowKey={'id'}
          bordered
          size={'small'}
        />
      )}
      <SellerShopOrderCreateModal
        openId={createModalOpenId}
        fevent={selectedFevent}
      ></SellerShopOrderCreateModal>
    </div>
  );
};
